import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

const LANGUAGES = [
  { code: 'pt-BR', flag: '🇧🇷', label: 'PT-BR' },
  { code: 'pt-PT', flag: '🇵🇹', label: 'PT-PT' },
  { code: 'es',    flag: '🇪🇸', label: 'ES' },
  { code: 'en',    flag: '🇺🇸', label: 'EN' },
];

interface LanguageSwitcherProps {
  /** 'dark' for white text headers, 'light' for slate headers */
  variant?: 'dark' | 'light';
}

export default function LanguageSwitcher({ variant = 'light' }: LanguageSwitcherProps) {
  const { i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const currentLang =
    LANGUAGES.find(l => l.code === i18n.language) ??
    LANGUAGES.find(l => i18n.language.startsWith(l.code.split('-')[0])) ??
    LANGUAGES[3];

  const handleSelect = (code: string) => {
    i18n.changeLanguage(code);
    setOpen(false);
  };

  // Close on outside click
  React.useEffect(() => {
    const onPointerDown = (e: PointerEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, []);

  const btnClass =
    variant === 'dark'
      ? 'flex items-center gap-1.5 rounded-lg px-2 py-1 text-sm font-medium text-white/90 hover:bg-white/15 transition-colors select-none'
      : 'flex items-center gap-1.5 rounded-lg px-2 py-1 text-sm font-medium text-slate-600 hover:bg-slate-100 transition-colors select-none';

  const dropdownClass =
    'absolute right-0 top-full mt-1 z-50 min-w-[130px] rounded-xl border border-slate-200 bg-white shadow-lg py-1 overflow-hidden';

  return (
    <div className="relative" ref={ref}>
      <button type="button" className={btnClass} onClick={() => setOpen(v => !v)} aria-label="Select language">
        <span className="text-base leading-none">{currentLang.flag}</span>
        <span className="hidden sm:inline">{currentLang.label}</span>
        <svg className="h-3 w-3 opacity-60" viewBox="0 0 12 12" fill="none">
          <path d="M2 4l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </button>

      {open && (
        <div className={dropdownClass}>
          {LANGUAGES.map(lang => (
            <button
              key={lang.code}
              type="button"
              onClick={() => handleSelect(lang.code)}
              className={`flex w-full items-center gap-2.5 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 transition-colors ${
                currentLang.code === lang.code ? 'font-semibold bg-slate-50' : ''
              }`}
            >
              <span className="text-base leading-none">{lang.flag}</span>
              <span>{lang.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
